import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiCheckCircle, FiXCircle } from "react-icons/fi";
import chatSupportLogService from "../../api/services/chatSupportLogService";
import csrOutreachService from "../../api/services/csrOutreachService";
import { PAGE_SIZE, TablePagination, TableSkeleton } from "../ChatSupportLog/chatSupportLogUi";
import { notifyApprovalsPendingChanged } from "../../utils/approvalsEvents";
import { collectProofUrls } from "../../utils/paymentProofUrls";
import { PaymentProofGallery } from "../../components/SubscriptionHistory/PaymentProofAttachments";

const THEME = { primary: "#FB565F" };
const inputCls =
  "border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 outline-none";

/** Team Lead only (Admin can also review). */
const ALLOWED = ["CSR Team Lead", "Admin"];
const VALID_TABS = new Set(["activity", "customerLogs", "premium"]);

function TabBtn({ active, onClick, children, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-xl text-sm font-semibold inline-flex items-center gap-2 ${
        active ? "text-white shadow" : "bg-gray-100 text-gray-600"
      }`}
      style={active ? { backgroundColor: THEME.primary } : {}}
    >
      {children}
      {typeof count === "number" && (
        <span
          className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
            active ? "bg-white/25 text-white" : "bg-amber-100 text-amber-800"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function StatusBadge({ status }) {
  const normalized = String(status || "pending").toLowerCase();
  const styles = {
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
    pending: "bg-amber-100 text-amber-800",
  };
  return (
    <span
      className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
        styles[normalized] || styles.pending
      }`}
    >
      {normalized}
    </span>
  );
}

function formatMinutes(mins) {
  const n = Number(mins) || 0;
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleString();
}

function buildListParams({ viewMode, statusFilter, search, fromDate, toDate, page }) {
  const approval_status =
    viewMode === "pending"
      ? "pending"
      : statusFilter === "approved" || statusFilter === "rejected"
        ? statusFilter
        : "history";
  return {
    approval_status,
    search: search.trim() || undefined,
    from: fromDate || undefined,
    to: toDate || undefined,
    page,
    limit: PAGE_SIZE,
  };
}

export default function ApprovalsPage() {
  const userRole = localStorage.getItem("adminUserRole") || "Admin";
  const isAllowed = ALLOWED.includes(userRole);
  const isLead = userRole === "CSR Team Lead";
  const isAdmin = userRole === "Admin";
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = VALID_TABS.has(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "activity";
  const initialView = searchParams.get("view") === "history" ? "history" : "pending";

  const [viewMode, setViewMode] = useState(initialView);
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activities, setActivities] = useState([]);
  const [customerLogs, setCustomerLogs] = useState([]);
  const [premiumRequests, setPremiumRequests] = useState([]);
  const [pendingCounts, setPendingCounts] = useState({ activity: 0, customerLogs: 0, premium: 0 });
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [approvalModal, setApprovalModal] = useState(null);

  // Deep-link from admin notifications: /approvals?tab=premium&view=pending
  useEffect(() => {
    const nextTab = searchParams.get("tab");
    const nextView = searchParams.get("view");
    if (VALID_TABS.has(nextTab) && nextTab !== tab) setTab(nextTab);
    if ((nextView === "pending" || nextView === "history") && nextView !== viewMode) {
      setViewMode(nextView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from URL only
  }, [searchParams]);

  const selectTab = (next) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    params.set("view", viewMode);
    setSearchParams(params, { replace: true });
  };

  const selectViewMode = (next) => {
    setViewMode(next);
    setPage(1);
    const params = new URLSearchParams(searchParams);
    params.set("tab", tab);
    params.set("view", next);
    setSearchParams(params, { replace: true });
  };

  const listParams = useMemo(
    () => buildListParams({ viewMode, statusFilter, search: debouncedSearch, fromDate, toDate, page }),
    [viewMode, statusFilter, debouncedSearch, fromDate, toDate, page]
  );

  const loadPendingCounts = useCallback(async () => {
    const countParams = { approval_status: "pending", page: 1, limit: 1 };
    try {
      const [actRes, custRes, premRes] = await Promise.all([
        chatSupportLogService.getPendingActivityLogs(countParams).catch(() => ({ total: 0 })),
        chatSupportLogService.getPendingCustomerLogs(countParams).catch(() => ({ total: 0 })),
        csrOutreachService.getPremiumRequests({ ...countParams, status: "pending" }).catch(() => ({ total: 0 })),
      ]);
      setPendingCounts({
        activity: actRes.total || 0,
        customerLogs: custRes.total || 0,
        premium: premRes.total || 0,
      });
      notifyApprovalsPendingChanged();
    } catch {
      // Keep existing counts on failure.
    }
  }, []);

  const loadTabData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "activity") {
        const res = await chatSupportLogService.getPendingActivityLogs(listParams);
        setActivities(res.activities || []);
        setPagination({
          page: res.page || page,
          total: res.total || 0,
          totalPages: res.totalPages || 1,
        });
      } else if (tab === "customerLogs") {
        const res = await chatSupportLogService.getPendingCustomerLogs(listParams);
        setCustomerLogs(res.logs || []);
        setPagination({
          page: res.page || page,
          total: res.total || 0,
          totalPages: res.totalPages || 1,
        });
      } else {
        const res = await csrOutreachService.getPremiumRequests({
          ...listParams,
          status: listParams.approval_status,
        });
        setPremiumRequests(res.requests || []);
        setPagination({
          page: res.page || page,
          total: res.total || 0,
          totalPages: res.totalPages || 1,
        });
      }
      if (viewMode === "pending") {
        await loadPendingCounts();
      }
    } catch {
      toast.error("Failed to load approval requests");
    } finally {
      setLoading(false);
    }
  }, [tab, listParams, page, viewMode, loadPendingCounts]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 400);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!isAllowed) return;
    loadTabData();
  }, [isAllowed, loadTabData]);

  useEffect(() => {
    setPage(1);
  }, [viewMode, tab, debouncedSearch, statusFilter, fromDate, toDate]);

  const submitApproval = async () => {
    if (!approvalModal) return;
    setSaving(true);
    try {
      const { kind, id, status, notes } = approvalModal;
      if (kind === "activity") {
        await chatSupportLogService.approveActivityLog(id, { status, notes });
        toast.success(status === "approved" ? "Activity approved — daily report updated" : "Activity rejected");
      } else if (kind === "customerLog") {
        await chatSupportLogService.approveCustomerLog(id, { status, notes });
        toast.success(status === "approved" ? "Customer log approved" : "Customer log rejected");
      } else if (kind === "premium") {
        await csrOutreachService.approvePremiumRequest(id, { status, notes });
        toast.success(
          status === "approved"
            ? "Manual subscription approved — premium activated and CSR agent recorded"
            : "Manual subscription request rejected"
        );
      }
      setApprovalModal(null);
      notifyApprovalsPendingChanged({ delta: -1 });
      await loadTabData();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Approval failed");
    } finally {
      setSaving(false);
    }
  };

  const paging = {
    page: pagination.page,
    totalPages: pagination.totalPages,
    totalItems: pagination.total,
    pageSize: PAGE_SIZE,
    setPage,
  };

  const renderHistoryMeta = (row) => (
    <div className="text-xs text-gray-500 space-y-0.5">
      <div>
        <StatusBadge status={row.tlApprovalStatus} />
      </div>
      {row.tlApproverName && <div>By: {row.tlApproverName}</div>}
      {row.tlApprovedAt && <div>{formatDateTime(row.tlApprovedAt)}</div>}
      {row.tlApprovalNotes && (
        <div className="max-w-[180px] truncate" title={row.tlApprovalNotes}>
          {row.tlApprovalNotes}
        </div>
      )}
    </div>
  );

  if (!isAllowed) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="p-4 sm:p-6 w-full min-w-0 overflow-x-hidden bg-gray-50/60 min-h-[calc(100vh-4rem)] space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Approvals</h1>
        <p className="text-sm text-gray-500 mt-1">
          Review pending requests from Chat Support (client activity &amp; customer logs) and CSR (premium payment
          proofs). Accepted and rejected requests appear in History.
          {(isLead || isAdmin) && (
            <span className="ml-1 text-gray-400">Visible to team lead{isAdmin ? " / admin" : ""} only.</span>
          )}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <TabBtn active={viewMode === "pending"} onClick={() => selectViewMode("pending")}>
          Pending
        </TabBtn>
        <TabBtn active={viewMode === "history"} onClick={() => selectViewMode("history")}>
          History
        </TabBtn>
      </div>

      <div className="flex flex-wrap gap-2">
        <TabBtn
          active={tab === "activity"}
          onClick={() => selectTab("activity")}
          count={viewMode === "pending" ? pendingCounts.activity : undefined}
        >
          Chat / Email / Social
        </TabBtn>
        <TabBtn
          active={tab === "customerLogs"}
          onClick={() => selectTab("customerLogs")}
          count={viewMode === "pending" ? pendingCounts.customerLogs : undefined}
        >
          Customer logs
        </TabBtn>
        <TabBtn
          active={tab === "premium"}
          onClick={() => selectTab("premium")}
          count={viewMode === "pending" ? pendingCounts.premium : undefined}
        >
          Manual subscriptions
        </TabBtn>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Search</label>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email, agent…"
              className={`${inputCls} w-full`}
            />
          </div>
          {viewMode === "history" && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`${inputCls} w-full`}
              >
                <option value="all">All (approved & rejected)</option>
                <option value="approved">Approved only</option>
                <option value="rejected">Rejected only</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">From date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className={`${inputCls} w-full`}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">To date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className={`${inputCls} w-full`}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <TableSkeleton rows={8} cols={6} />
      ) : tab === "activity" ? (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Agent</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">User</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Contact</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Channel</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Time</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Date</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Notes</th>
                  {viewMode === "history" && (
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Decision</th>
                  )}
                  {viewMode === "pending" && (
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {activities.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100 align-top">
                    <td className="px-3 py-2 text-gray-700">{row.agentName || row.agentEmail || "—"}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{row.clientName || "—"}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      <div>{row.clientEmail || "—"}</div>
                      <div>{row.clientPhone || ""}</div>
                    </td>
                    <td className="px-3 py-2 capitalize">{row.channel}</td>
                    <td className="px-3 py-2">{formatMinutes(row.timeSpentMinutes)}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {typeof row.logDate === "string" ? row.logDate.slice(0, 10) : "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-500 max-w-[160px] truncate" title={row.notes || ""}>
                      {row.notes || "—"}
                    </td>
                    {viewMode === "history" && <td className="px-3 py-2">{renderHistoryMeta(row)}</td>}
                    {viewMode === "pending" && (
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              setApprovalModal({
                                kind: "activity",
                                id: row.id,
                                status: "approved",
                                notes: "",
                                label: row.clientName || row.channel,
                              })
                            }
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700"
                          >
                            <FiCheckCircle /> Accept
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setApprovalModal({
                                kind: "activity",
                                id: row.id,
                                status: "rejected",
                                notes: "",
                                label: row.clientName || row.channel,
                              })
                            }
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-rose-50 text-rose-700"
                          >
                            <FiXCircle /> Reject
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {!activities.length && (
                  <tr>
                    <td colSpan={viewMode === "history" ? 8 : 8} className="px-3 py-12 text-center text-gray-400">
                      {viewMode === "pending"
                        ? "No pending chat / email / social records."
                        : "No history records found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePagination paging={paging} label="records" />
        </div>
      ) : tab === "customerLogs" ? (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Agent</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Customer</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Premium</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Date</th>
                  {viewMode === "history" && (
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Decision</th>
                  )}
                  {viewMode === "pending" && (
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {customerLogs.map((log) => (
                  <tr key={log.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-700">{log.agentName || "—"}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{log.customerName}</td>
                    <td className="px-3 py-2 text-gray-600">{log.premiumDuration || "—"}</td>
                    <td className="px-3 py-2 text-gray-600">{log.relationshipStatus || "—"}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {typeof log.logDate === "string" ? log.logDate.slice(0, 10) : "—"}
                    </td>
                    {viewMode === "history" && <td className="px-3 py-2">{renderHistoryMeta(log)}</td>}
                    {viewMode === "pending" && (
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              setApprovalModal({
                                kind: "customerLog",
                                id: log.id,
                                status: "approved",
                                notes: "",
                                label: log.customerName,
                              })
                            }
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700"
                          >
                            <FiCheckCircle /> Accept
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setApprovalModal({
                                kind: "customerLog",
                                id: log.id,
                                status: "rejected",
                                notes: "",
                                label: log.customerName,
                              })
                            }
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-rose-50 text-rose-700"
                          >
                            <FiXCircle /> Reject
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {!customerLogs.length && (
                  <tr>
                    <td colSpan={viewMode === "history" ? 6 : 6} className="px-3 py-12 text-center text-gray-400">
                      {viewMode === "pending"
                        ? "No pending customer logs."
                        : "No history records found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePagination paging={paging} label="records" />
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Customer</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Agent</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Amount</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Plan</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Proof</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Notes</th>
                  {viewMode === "history" && (
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Decision</th>
                  )}
                  {viewMode === "pending" && (
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {premiumRequests.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100 align-top">
                    <td className="px-3 py-2">
                      <p className="font-medium">{r.customerName || "—"}</p>
                      <p className="text-xs text-gray-400">{r.customerEmail}</p>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.agentName || "—"}</td>
                    <td className="px-3 py-2 font-semibold">
                      {r.currency} {Number(r.amount || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-xs">{r.planName || `Plan #${r.subscriptionPlanId}`}</td>
                    <td className="px-3 py-2">
                      <PaymentProofGallery urls={collectProofUrls(r)} />
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 max-w-[160px]">{r.notes || "—"}</td>
                    {viewMode === "history" && <td className="px-3 py-2">{renderHistoryMeta(r)}</td>}
                    {viewMode === "pending" && (
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              setApprovalModal({
                                kind: "premium",
                                id: r.id,
                                status: "approved",
                                notes: "",
                                label: r.customerName || r.customerEmail,
                              })
                            }
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700"
                          >
                            <FiCheckCircle /> Accept
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setApprovalModal({
                                kind: "premium",
                                id: r.id,
                                status: "rejected",
                                notes: "",
                                label: r.customerName || r.customerEmail,
                              })
                            }
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-rose-50 text-rose-700"
                          >
                            <FiXCircle /> Reject
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {!premiumRequests.length && (
                  <tr>
                    <td colSpan={viewMode === "history" ? 7 : 7} className="px-3 py-12 text-center text-gray-400">
                      {viewMode === "pending"
                        ? "No pending premium payment proofs."
                        : "No history records found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePagination paging={paging} label="records" />
        </div>
      )}

      {approvalModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <h2 className="font-bold text-gray-900 mb-1">
              {approvalModal.status === "approved" ? "Accept request" : "Reject request"}
            </h2>
            <p className="text-sm text-gray-500 mb-3">{approvalModal.label}</p>
            <textarea
              value={approvalModal.notes}
              onChange={(e) => setApprovalModal((m) => ({ ...m, notes: e.target.value }))}
              className={`${inputCls} w-full min-h-[80px] mb-4`}
              placeholder="Optional notes…"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setApprovalModal(null)}
                className="px-4 py-2 text-sm rounded-lg border"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitApproval}
                disabled={saving}
                className="px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{
                  backgroundColor: approvalModal.status === "approved" ? "#10b981" : THEME.primary,
                }}
              >
                {saving ? "Saving…" : approvalModal.status === "approved" ? "Accept" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
